import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Apple, Info } from "lucide-react";

/** Compacte inline knop + modal met 3-staps iOS "Add to Home Screen" uitleg
 *  met SVG-mockups van Safari share-sheet + home screen. Geen echte screenshots
 *  nodig — de SVGs blijven altijd scherp en tenant-neutraal. */
export default function IOSInstallHelp({ compact = false }) {
  const [open, setOpen] = useState(false);
  const isIOS = typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs font-medium text-sky-800 hover:text-sky-900 underline decoration-dotted underline-offset-2"
        data-testid="ios-install-help-btn"
      >
        <Apple className="h-3 w-3" />
        {compact ? "iPhone-tip" : (isIOS ? "Zo stel je het in op iPhone" : "Op iPhone? Zo werkt het")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="ios-install-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Apple className="h-4 w-4" /> Meldingen op iPhone in 3 stappen
            </DialogTitle>
            <DialogDescription>
              Op iPhone werken browser-meldingen alleen als je de app eerst aan je beginscherm toevoegt. Daarna komt BKR-rood binnen als een gewone iPhone-melding.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
            <IOSStep n={1} title="Open in Safari" body="Werkt alleen via Safari — niet Chrome of Firefox op iPhone.">
              <PhoneMock>
                <BrowserBar />
                <UrlBar />
                <div style={{ position: "absolute", bottom: 6, right: 22, width: 30, height: 30, borderRadius: 6, background: "#e5e7eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ShareIcon />
                </div>
                <ArrowMark toX={82} toY={92} />
              </PhoneMock>
            </IOSStep>

            <IOSStep n={2} title="Delen → Zet op beginscherm" body="Scroll in het menu naar beneden tot je 'Zet op beginscherm' ziet.">
              <PhoneMock>
                <div style={{ position: "absolute", left: 6, right: 6, top: 12, bottom: 12, borderRadius: 10, background: "#f3f4f6", padding: 6 }}>
                  <div style={{ height: 8, background: "#d1d5db", borderRadius: 2, marginBottom: 6 }} />
                  <div style={{ height: 8, background: "#d1d5db", borderRadius: 2, marginBottom: 6 }} />
                  <div style={{ height: 12, background: "#c7d2fe", borderRadius: 3, marginBottom: 6, display: "flex", alignItems: "center", padding: "0 4px" }}>
                    <div style={{ width: 6, height: 6, background: "#4338ca", borderRadius: 1, marginRight: 4 }} />
                    <div style={{ height: 4, flex: 1, background: "#4338ca", opacity: 0.7, borderRadius: 1 }} />
                  </div>
                  <div style={{ height: 8, background: "#d1d5db", borderRadius: 2 }} />
                </div>
                <ArrowMark toX={50} toY={60} />
              </PhoneMock>
            </IOSStep>

            <IOSStep n={3} title="Open vanaf beginscherm" body="Tik op het nieuwe app-icoon. Toestaan bij de eerste push-vraag.">
              <PhoneMock>
                <div style={{ position: "absolute", left: 8, right: 8, top: 20, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} style={{ aspectRatio: "1/1", background: i === 4 ? "var(--sage,#2D4A3E)" : "#e5e7eb", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 8, fontWeight: 700 }}>
                      {i === 4 ? "W" : ""}
                    </div>
                  ))}
                </div>
                <ArrowMark toX={45} toY={55} />
              </PhoneMock>
            </IOSStep>
          </div>

          <div className="mt-2 p-2 rounded border border-neutral-200 bg-neutral-50 text-[11px] text-neutral-600 flex items-start gap-2">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <div>
              <strong>Belangrijk:</strong> push-meldingen werken op iOS 16.4 of hoger. Controleer via <em>Instellingen → Algemeen → Info</em>. Op oudere iPhones ontvang je BKR-alarmen alleen per e-mail.
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setOpen(false)} data-testid="ios-help-close" style={{ background: "var(--sage)" }} className="text-white">
              Begrepen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function IOSStep({ n, title, body, children }) {
  return (
    <div className="border border-neutral-200 rounded-lg p-2 bg-white">
      <div className="flex items-center gap-1.5 mb-1">
        <div className="w-5 h-5 rounded-full bg-[color:var(--sage)] text-white flex items-center justify-center text-[10px] font-bold">{n}</div>
        <div className="text-xs font-semibold text-neutral-800">{title}</div>
      </div>
      <div className="flex justify-center py-1">{children}</div>
      <div className="text-[10px] text-neutral-500 leading-snug">{body}</div>
    </div>
  );
}

function PhoneMock({ children }) {
  return (
    <div style={{
      position: "relative", width: 90, height: 140,
      background: "#1f2937", borderRadius: 12, padding: 3,
    }}>
      <div style={{ position: "absolute", left: "50%", top: 4, transform: "translateX(-50%)", width: 24, height: 3, background: "#111827", borderRadius: 2 }} />
      <div style={{ position: "absolute", inset: 6, background: "white", borderRadius: 8, overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

function BrowserBar() {
  return <div style={{ height: 10, background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }} />;
}

function UrlBar() {
  return (
    <div style={{ padding: "4px 6px" }}>
      <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4, display: "flex", alignItems: "center", padding: "0 4px" }}>
        <div style={{ width: 4, height: 4, background: "#10b981", borderRadius: 2, marginRight: 3 }} />
        <div style={{ height: 3, flex: 1, background: "#9ca3af", borderRadius: 1 }} />
      </div>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v14M6 8l6-6 6 6" />
      <rect x="4" y="14" width="16" height="8" rx="2" />
    </svg>
  );
}

function ArrowMark({ toX, toY }) {
  // Pulse red circle to draw attention
  return (
    <div style={{
      position: "absolute", left: `${toX - 8}%`, top: `${toY - 8}%`, width: 16, height: 16,
      borderRadius: "50%", background: "rgba(220,38,38,0.35)", border: "2px solid #dc2626",
      animation: "pulse 1.5s ease-in-out infinite",
    }} />
  );
}
